import { keys, values } from 'lodash';

const idFunc = item => item.id;

export const stringToBool = (strValue) => {
  const lowered = strValue && strValue.toLowerCase().trim();
  switch (lowered) {
    // this or nothing
    case 'true':
      return true;

    case 'false':
    case null:
    case undefined:
    default:
      return false;
  }
};

/**
 * Takes a list of objects and puts them into a map where the key is obtained with
 * propFunc
 * @param {any[]} objects - List of objects
 * @param {(o) => any} propFunc - Function used to get the object key
 * @param {(o) => any} itemFunc - Function used to mutate the object if desired. Default
 *   value simply gets the object id.
 */
export const propertyReducer = (objects, propFunc, itemFunc = idFunc) => {
  return objects.reduce(
    (out, item) => {
      //handle both null and undefined
      if (!item) {
        return {};
      }
      const prop = propFunc(item);
      const val = itemFunc(item);

      if (prop !== undefined && val !== undefined) {
        out[prop] = val;
      }

      return out;
    },
    {});
};

/**
 * Takes a list of objects and transforms it into a map where they key is obtained by
 * the prop and the value is a list of all objects that share the same key value.
 * @param {any[]} objects - List of objects
 * @param {string} prop - The property to accumulate over.
 */
export const propertyAccumulatorReducer = (objects, prop) => {
  return accumulatorReducerByFunc(objects, o => o[prop], o => o);
};

/**
 * Creates map where given prop values are the keys but the prop is an array and they will
 * be split out
 * @param {any[]} objects - List of objects
 * @param {string} prop - The property to accumulate over.
 */
export const propertyArrayAccumulatorReducer = (objects, prop) => {
  return accumulatorReducerByFunc(objects, o => o[prop], o => o, true);
};

/**
 * Takes a list of objects and transforms it into a map where the key is obtained by
 * the propFunc and the value is a list of all objects that share the same key value.
 * @param {any[]} objects - List of objects
 * @param {(o) => any} propFunc - Function used to retrieve the property
 * @param {boolean} isArray - If the property value obtained from propFunc is an array or
 *   not. If it is, the values will be split into their own keys as well.
 */
export const propertyAccumulatorReducerByFunc = (objects, propFunc, isArray) => {
  return accumulatorReducerByFunc(objects, propFunc, o => o, isArray);
};

/**
 * Takes a list of objects and transforms it into a map where each object is placed under
 * a key obtained by keyFunc(object) and a value obtained by valueFunc(object, key)
 * the propFunc
 * @param {any[]} objects - List of objects
 * @param {(o) => any} keyFunc - Function used to retrieve the key
 * @param {(o, key) => any} valueFunc - Function used to retrieve the value
 * @param {boolean} keyIsArray - If the key obtained from keyFunc is an array or
 *   not. If it is, the values will be split into their own keys as well.
 * @param {boolean} valuePerKey - If true, will call valueFunc with each key to
 *  be added to produce a possibly new value per key
 */
export const accumulatorReducerByFunc = (
  objects,
  keyFunc,
  valueFunc,
  keyIsArray,
  valuePerKey
) => reducerByFunc(
  objects,
  keyFunc,
  valueFunc,
  keyIsArray,
  valuePerKey,
  {},
  (result, key, value) => {
    result[key] = [...(result[key] || []), value];
  }
);

export const propertyReduceToSet = (objects, prop) => {
  return reduceToSetByFunc(objects, o => o[prop], (_o, k) => k);
};

/**
 * Takes a list of objects and transforms it into a Set where a value to add
 * is obtained from valueFunc(object, key) and a key from keyFunc(object)
 * @param {any[]} objects - List of objects
 * @param {(o) => any} keyFunc - Function used to retrieve the key
 * @param {(o, key) => any} valueFunc - Function used to retrieve the value
 * @param {boolean} keyIsArray - If the key obtained from keyFunc is an array or
 *   not. If it is, the values will be split into their own keys as well.
 * @param {boolean} valuePerKey - If true, will call valueFunc with each key to
 *  be added to produce a possibly new value per key
 */
export const reduceToSetByFunc = (
  objects,
  keyFunc,
  valueFunc,
  keyIsArray,
  valuePerKey
) => reducerByFunc(
  objects,
  keyFunc,
  valueFunc,
  keyIsArray,
  valuePerKey,
  new Set(),
  (result, _key, pushValue) => result.add(pushValue)
);

/**
 * Takes a list of objects and transforms it into the accumulator provided
 * where a value to add is obtained from valueFunc(object, key)
 * and a key from keyFunc(object)
 * @param {any[]} objects - List of objects
 * @param {(o) => any} keyFunc - Function used to retrieve the key
 * @param {(o, key) => any} valueFunc - Function used to retrieve the value
 * @param {boolean} keyIsArray - If the key obtained from keyFunc is an array or
 *   not. If it is, the values will be split into their own keys as well.
 * @param {boolean} valuePerKey - If true, will call valueFunc with each key to
 *  be added to produce a possibly new value per key
 * @param accumulator - a collection that can accrue new values
 * @param {(acc, key, value) => void} accumulatorFunc - a func that is called
 *  which will modify the accumulator by reference
 */
export const reducerByFunc = (
  objects,
  keyFunc,
  valueFunc,
  keyIsArray,
  valuePerKey,
  accumulator,
  accumulatorFunc
) => {
  const reduced = objects.reduce((acc, object) => {
    const key = object && keyFunc(object);
    if (!key) return acc;

    const value = !valuePerKey && valueFunc(object, key);
    const keysToAdd = keyIsArray ? key : [key];
    keysToAdd.forEach(key => {
      const pushValue = valuePerKey ?
        valueFunc(object, key) :
        value;

      accumulatorFunc(acc, key, pushValue);
    });

    return acc;
  }, accumulator);
  return reduced;
};

/**
 * Take two arrays which by themselves have uniq data and combine them into one array
 * of uniq results. This is faster then using lodash uniq or uniqBy since we know that
 * all arrays are uniq to start with instead of doing a comparison between all data in the
 * resulting array
 * @param {o => any} propFunc - Return the prop value for an item that will be used to
 *   ensure uniqness
 * @param {any[][]} arrays - All the arrays to combine
 */
export const uniqArrayCombinator = (propFunc, ...arrays) => {
  const reductions = arrays.map(a => propertyReducer(a, propFunc, i => i));
  let result = {};
  reductions.forEach(r => result = { ...result, ...r });

  return values(result);
};

/**
 * Takes an object with deep values and returns a map of all keys found at the second
 * level of the object
 */
export const keysOfValuesReducer = (object) => {
  if (!object) { return []; }
  return keys(object).reduce((result, key) => {
    const valKeys = keys(object[key]);
    valKeys.forEach(valKey => result[valKey] = valKey);
    return result;
  }, {});
};

/**
 * Takes in two string correctly formatted representations of product
 * versions ('major.minor.patch.build') and returns:
 *  0 - if they are the same version
 *  < 0 - if b is a higher version than a
 *  > 0 - if a is a higher version b
 */
export const versionComparer = (a, b) => {
  const aSplit = a.split('.');
  const bSplit = b.split('.');

  // find first of major, minor, patch, build version not equal
  for (let index = 0; index < 4; index++) {
    const aVer = parseInt(aSplit[index]);
    const bVer = parseInt(bSplit[index]);
    if (aVer !== bVer) {
      return aVer - bVer;
    }
  }
  return 0;
};

/**
 * Takes an array and sums all key value pairs whose key starts
 * with the given keyName. Function then returns sum
 */
export const sumArrayByKeyName = (array, keyName) => {
  if (!array) {
    return 0;
  }

  let sum = 0;
  Object.keys(array).forEach(key => {
    if (key.startsWith(keyName)) {
      sum += +array[key];
    }
  });

  return sum;
};
/**
 * Sorts an array of items in 2 different ways:
 * With prioritized items at the beginning, and all others in alphabetical
 * order at the end.
 * @param sortOrder The array of items that should be sorted in the order they
 * appear. Any items that are NOT in this array will be sorted AFTER any that
 * are, in alphabetical order.
 * @param items The array of items to be sorted
 */
export const customPrioritySort = (sortOrder = [], items) => {
  return items.sort((a, b) => {
    a = a.toUpperCase();
    b = b.toUpperCase();
    const aIndex = sortOrder.indexOf(a);
    const bIndex = sortOrder.indexOf(b);
    if (aIndex > -1 && bIndex > -1) {
      if (aIndex < bIndex)
        return -1;
      else
        return 1;
    } else {
      if (aIndex > -1)
        return -1;
      if (bIndex > -1)
        return 1;
      if (a < b)
        return -1;
      if (a > b)
        return 1;
      if (a === b)
        return 0;
    }
  });
};


// WEBPACK FOOTER //
// src/helpers/utils.js